import { generateCodeChallenge } from './authorizationCodeWithPkce';
import { SystemStorageType } from '../App/model/system-storage-types';
import { tokenStorageItemKey } from './auth.consts';

/**
 * @ngdoc service
 * @name mx.shell.AuthService
 * @module mx.shell
 * @description Represents basic logic for an application authentication
 */
export class AuthenticationService {
	static $inject = [
		'mxAuthSessionClient',
		'mx.shell.Config',
		'mx.shell.AuthBehaviorProviderService',
		'mxAuthLocationTracker',
		'mxAuthClient',
		'mx.SolutionBuilderAgent.Http',
		'mx.internationalization',
		'$mdDialog',
		'mx.shell.DataStorage',
	];

	constructor(mxAuthSessionClient, mxConfig, authBehavior, mxAuthLocationTracker, mxAuthClient, http, i18n, $mdDialog, dataStorage) {
		this.localStorageSignoutKey = 'mx.login.signout';

		Object.assign(this, {
			mxAuthSessionClient,
			mxConfig,
			authBehavior,
			mxAuthLocationTracker,
			mxAuthClient,
			http,
			i18n,
			$mdDialog,
			localStorage: dataStorage.getStorage(SystemStorageType.LocalStorage),
		});

		mxAuthSessionClient.onExpired(() => this._onSessionExpired());
	}

	async _onSessionExpired() {
		if (!this.sessionPromise) {
			return;
		}
		const { mxAuthClient, authBehavior, $mdDialog, i18n } = this;

		this.sessionPromise = null;
		await authBehavior.sessionExpired();

		const confirm = $mdDialog
			.confirm()
			.title(i18n.get('shell.authentication.sessionExpired'))
			.ariaLabel(i18n.get('shell.authentication.sessionExpired'))
			.ok(i18n.get('shell.authentication.confirmGoToLogin'))
			.cancel(i18n.get('shell.authentication.stayHereAnyway'));

		$mdDialog.show(confirm).then(() => {
			mxAuthClient.startLogoutWorkflow(undefined, true);
		});
	}

	async configAuthClient() {
		if (this._authCfg) {
			return this._authCfg;
		}

		const authCfg = this.getAuthConfig();

		if (!this.mxConfig.isDebugMode && authCfg?.oauth2 && authCfg.active) {
			const oauth2 = authCfg.oauth2;

			oauth2.redirect_uri = this.http.fixRelationLink(oauth2.redirect_uri);
			oauth2.endpoints = oauth2.endpoints || {};
			oauth2.endpoints.authorize = this.http.fixRelationLink(oauth2.endpoints.authorize);

			const authParams = await this.getAuthParams();

			oauth2.endpoints.signout = authParams.StsSignOutUrl;

			this.mxAuthClient.configure(oauth2);
			this.mxAuthSessionClient.configure({ persistent: oauth2.persistent });
		}

		this._authCfg = authCfg;

		return authCfg;
	}

	/**
	 * @async
	 * @ngdoc method
	 * @name mx.shell.AuthService#login
	 *
	 * @description
	 * Tries to authenticate a user using via external oAuth2 endpoint.<br>
	 * Also passes parameters, specified in {@ref config.json configuration file}
	 * under 'authentication' section with a name 'params'
	 *
	 * @return {promise} Promise resolves as session
	 */
	async login() {
		if (this.sessionPromise) {
			return this.sessionPromise;
		}

		const authCfg = await this.configAuthClient();
		if ($.isEmptyObject(authCfg)) {
			return;
		}

		const params = authCfg.params || {};
		const passThrough = authCfg.passThrough;

		//a passThrough url parameter can be used to signal the authentication server to show
		//the login dialog, even if autologin is configured. It must be the last parameter of the request
		//sample: https://myserver.com/wm&MyPassthrough will add a "MyPassthrough=true" to the auth request
		const targetLocation = this.mxAuthLocationTracker.getTargetLocation();
		if (passThrough && targetLocation && targetLocation.indexOf(passThrough, targetLocation.length - passThrough.length) > -1) {
			params[passThrough] = 'true';
		}

		const isAuthCodeResponse = /^.*?code=.+&state=[^&]*$/.test(targetLocation);
		if (!isAuthCodeResponse && authCfg.oauth2 && !authCfg.oauth2.auth_with_implicit) {
			params.allowAuthWithPkce = generateCodeChallenge();
		}

		const isSignout = this.localStorage.get(this.localStorageSignoutKey);
		if (isSignout) {
			window.localStorage.removeItem(this.localStorageSignoutKey);
			params.redirect_to_signout = true;
		}

		const currentToken = this.localStorage.get(tokenStorageItemKey);

		this.sessionPromise = this.mxAuthSessionClient.makeSessionAvailable(params);
		this.sessionPromise.then((session) => {
			this.authBehavior.sessionEstablished(session, session.raw !== currentToken);
		});
		return this.sessionPromise;
	}

	/**
	 * @async
	 * @ngdoc method
	 * @name mx.shell.AuthService#logout
	 *
	 * @description
	 * Send user to oAuth2 log-out endpoint.
	 */
	async logout() {
		const { mxAuthClient, authBehavior, mxAuthSessionClient } = this;
		const config = await this.configAuthClient();

		if ($.isEmptyObject(config)) {
			return;
		}

		if (!this.sessionPromise) {
			mxAuthClient.startLogoutWorkflow(undefined, true);
			return;
		}

		this._logoutPromise =
			this._logoutPromise ||
			new Promise(async (resolve) => {
				await authBehavior.beforeSessionDestroy();
				this.sessionPromise = null;
				mxAuthSessionClient.destroySession();

				this._logoutPromise = null;
				resolve();
			});
		return this._logoutPromise;
	}

	setupTokenRemovalListener() {
		const storageListener = (event) => {
			if (event.key === tokenStorageItemKey && !event.newValue) {
				this.mxAuthClient.startLogoutWorkflow(undefined, true);
			}
		};

		// Setup the listener
		window.addEventListener('storage', storageListener);

		// Return cleanup function
		return () => window.removeEventListener('storage', storageListener);
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.AuthService#getToken
	 * @async
	 *
	 * @description
	 * Tries to get a token
	 *
	 * @return {promise} Promise resolves as token
	 */
	async getToken() {
		if (this.mxConfig.isDebugMode) {
			return 'debug_token';
		}
		const authCfg = this.mxConfig.get('authentication') || {};

		if (authCfg.active && this.sessionPromise) {
			const session = await this.sessionPromise;
			return session.raw;
		}
	}

	async getSessionExpirationTime() {
		if (!this.sessionPromise) {
			return null;
		}
		const session = await this.sessionPromise;
		return session.claims.exp * 1000;
	}

	async isAuthenticated() {
		return Boolean(await this.getToken());
	}

	getAuthConfig() {
		return this.mxConfig.get('authentication') ?? {};
	}

	async getAuthParams() {
		const authCfg = this.getAuthConfig();
		const authParams = await this.http.get(`/api/login/params?clientId=${authCfg.oauth2.clientid}`);
		authParams.StsSignOutUrl = this.http.formatUrl(authParams.StsSignOutUrl);
		return authParams;
	}

	async isSignUpEnabled() {
		const authParams = await this.getAuthParams();
		return Boolean(authParams.AllowUserRegistration);
	}
}
